import React, { useState } from 'react';
import { 
  User, 
  Building2, 
  Mail, 
  Phone, 
  MapPin, 
  Instagram, 
  Calendar,
  Edit3,
  Save,
  X,
  Camera
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { dataService } from '../../services/dataService';

const Profile: React.FC = () => {
  const { userData, tenantData, refreshTenantData } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    businessName: tenantData?.businessName || '',
    address: tenantData?.address || '',
    instagramHandle: tenantData?.instagramHandle || ''
  });

  const handleSave = async () => {
    if (!tenantData) return;

    try {
      setLoading(true);
      
      // Update tenant data
      await dataService.updateTenant(tenantData.id, {
        businessName: formData.businessName,
        address: formData.address || undefined,
        instagramHandle: formData.instagramHandle || undefined
      });

      // Refresh the data
      await refreshTenantData();
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      businessName: tenantData?.businessName || '',
      address: tenantData?.address || '',
      instagramHandle: tenantData?.instagramHandle || ''
    });
    setIsEditing(false);
  };

  const formatDate = (date: Date | undefined) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (!userData || !tenantData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-page-enter">
      {/* Header */}
      <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-6 animate-fade-in-up">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div className="flex items-center space-x-3 sm:space-x-4">
            <div className="relative">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center">
                <User className="w-8 h-8 sm:w-10 sm:h-10 text-primary-600 dark:text-primary-400" />
              </div>
              <button className="absolute -bottom-1 -right-1 p-1.5 sm:p-2 bg-white/90 dark:bg-gray-700/90 backdrop-blur-md rounded-full shadow-md border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors duration-200">
                <Camera className="w-3 h-3 sm:w-4 sm:h-4 text-gray-600 dark:text-gray-400" />
              </button>
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 dark:text-white truncate">
                {userData.firstName} {userData.lastName}
              </h1>
              <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300">
                {userData.role.charAt(0).toUpperCase() + userData.role.slice(1)}
              </p>
              <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                Member since {formatDate(userData.createdAt)}
              </p>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
            {!isEditing ? (
              <button
                onClick={() => setIsEditing(true)}
                className="btn-secondary flex items-center justify-center space-x-2 text-sm py-2 px-3 sm:px-4"
              >
                <Edit3 className="w-4 h-4" />
                <span>Edit Profile</span>
              </button>
            ) : (
              <>
                <button
                  onClick={handleCancel}
                  className="btn-secondary flex items-center justify-center space-x-2 text-sm py-2 px-3 sm:px-4"
                  disabled={loading}
                >
                  <X className="w-4 h-4" />
                  <span>Cancel</span>
                </button>
                <button
                  onClick={handleSave}
                  className="btn-primary flex items-center justify-center space-x-2 text-sm py-2 px-3 sm:px-4"
                  disabled={loading}
                >
                  <Save className="w-4 h-4" />
                  <span>{loading ? 'Saving...' : 'Save Changes'}</span>
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Personal Information */}
      <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 animate-fade-in-up animation-delay-200">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6 flex items-center">
          <User className="w-5 h-5 mr-2 text-primary-600 dark:text-primary-400" />
          Personal Information
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Full Name</label>
            <div className="flex items-center space-x-3 py-2">
              <span className="text-gray-900 dark:text-white">{tenantData.name}</span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Email Address</label>
            <div className="flex items-center space-x-3 py-2">
              <Mail className="w-4 h-4 text-gray-400 dark:text-gray-500" />
              <span className="text-gray-900 dark:text-white">{tenantData.email}</span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Phone Number</label>
            <div className="flex items-center space-x-3 py-2">
              <Phone className="w-4 h-4 text-gray-400 dark:text-gray-500" />
              <span className="text-gray-900 dark:text-white">{tenantData.phone || 'Not provided'}</span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">User ID</label>
            <div className="flex items-center space-x-3 py-2">
              <span className="text-gray-600 dark:text-gray-400 font-mono text-sm">{userData.id}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Business Information */}
      <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 animate-fade-in-up animation-delay-300">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6 flex items-center">
          <Building2 className="w-5 h-5 mr-2 text-primary-600 dark:text-primary-400" />
          Business Information
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Business Name</label>
            {isEditing ? (
              <input
                type="text"
                value={formData.businessName}
                onChange={(e) => setFormData(prev => ({ ...prev, businessName: e.target.value }))}
                className="input-field"
              />
            ) : (
              <div className="flex items-center space-x-3 py-2">
                <Building2 className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                <span className="text-gray-900 dark:text-white">{tenantData.businessName}</span>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Tenant ID</label>
            <div className="flex items-center space-x-3 py-2">
              <span className="text-gray-600 dark:text-gray-400 font-mono text-sm">{tenantData.id}</span>
            </div>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Business Address</label>
            {isEditing ? (
              <textarea
                value={formData.address}
                onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                className="input-field"
                rows={3}
                placeholder="Enter business address"
              />
            ) : (
              <div className="flex items-start space-x-3 py-2">
                <MapPin className="w-4 h-4 text-gray-400 dark:text-gray-500 mt-1" />
                <span className="text-gray-900 dark:text-white">{tenantData.address || 'Not provided'}</span>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Instagram Handle</label>
            {isEditing ? (
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400">@</span>
                <input
                  type="text"
                  value={formData.instagramHandle}
                  onChange={(e) => setFormData(prev => ({ ...prev, instagramHandle: e.target.value }))}
                  className="input-field pl-8"
                  placeholder="cafelawn"
                />
              </div>
            ) : (
              <div className="flex items-center space-x-3 py-2">
                <Instagram className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                <span className="text-gray-900 dark:text-white">
                  {tenantData.instagramHandle ? `@${tenantData.instagramHandle}` : 'Not provided'}
                </span>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Member Since</label>
            <div className="flex items-center space-x-3 py-2">
              <Calendar className="w-4 h-4 text-gray-400 dark:text-gray-500" />
              <span className="text-gray-900 dark:text-white">{formatDate(tenantData.createdAt)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Account Statistics */}
      <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 animate-fade-in-up animation-delay-400">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Account Statistics</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                          <div className="text-center p-4 bg-primary-50 dark:bg-primary-900/20 rounded-lg border border-primary-200 dark:border-primary-800 animate-card-hover">
                  <div className="text-2xl font-bold text-primary-600 dark:text-primary-400">Active</div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Account Status</div>
          </div>
          
          <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800 animate-card-hover">
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">Owner</div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Role</div>
          </div>
          
          <div className="text-center p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800 animate-card-hover">
            <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
              {tenantData.isQrOrderingEnabled ? 'Enabled' : 'Disabled'}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">QR Ordering</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile; 